import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {
    add,
    update
} from '../services/ProductsService';
import {createProductQueryOptions} from '../query-options/createProductQueryOptions';
import {Product} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {buildCreateProductInput, buildUpdateProductInput} from '../services/buildProductInput';
import {productsQueryKeys} from './productsQueryKeys';

export const useProduct = (productId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const productQuery = useQuery({
                                      ...createProductQueryOptions(productId!),
                                      enabled: !!productId
                                  });

    // ['products'] is the root of the list AND the detail keys, so one invalidation
    // reaches both (§8).
    const invalidate = () => queryClient.invalidateQueries({queryKey: productsQueryKeys.all});

    // CREATE MUTATION
    const createMutation = useMutation({
                                           mutationFn: (values: Product) => add(buildCreateProductInput(values)),
                                           onSuccess: () => {
                                               notifySuccess(I18n.get('Product created'));
                                               invalidate();
                                           },
                                           onError: (error) => {
                                               notifyError(error);
                                           }
                                       });

    // UPDATE MUTATION
    const updateMutation = useMutation({
                                           mutationFn: (values: Product) => {
                                               if (!productId) throw new Error('productId is required parameter');
                                               return update(productId, buildUpdateProductInput(values) as Product);
                                           },
                                           onSuccess: () => {
                                               notifySuccess(I18n.get('Product updated'));
                                               invalidate();
                                           },
                                           onError: (error) => {
                                               notifyError(error);
                                           }
                                       });

    // Resolve true once the save lands and false when it is rejected, never throw: the
    // drawer awaits this and closes only on true, so a server-side rejection keeps the
    // operator's input on screen (§6, §7). The mutation's onError has already toasted.
    const settle = (run: Promise<unknown>) => run.then(() => true, () => false);

    return {
        record: productQuery.data,
        loading: productQuery.isLoading,
        isError: productQuery.isError,
        fetchRecord: productQuery.refetch,
        createRecord: (values: Product) => settle(createMutation.mutateAsync(values)),
        updateRecord: (values: Product) => settle(updateMutation.mutateAsync(values)),
        createMutation,
        updateMutation,
    };
};
